/**
 * HTTP parameter sanitization utilities.
 *
 * Uses an allow-list approach per Fortify's Header Manipulation remediation
 * guidance: only characters explicitly permitted for each context are kept;
 * everything else (including CR/LF and other control characters that could
 * be used for header injection) is stripped.
 */

/** Alphanumeric + spaces + common punctuation safe for general search strings */
const SEARCH_STRING_ALLOWED = /[^a-zA-Z0-9 _\-.,()[\]+@]/g;

/** Alphanumeric + underscore + hyphen for enum-style filter/sort identifiers */
const IDENTIFIER_ALLOWED = /[^a-zA-Z0-9_\-]/g;

/** Rock file names: alphanumeric + dot + underscore + hyphen */
const FILENAME_ALLOWED = /[^a-zA-Z0-9._\-]/g;

/** Simulation names typed by the user: alphanumeric + spaces + common punctuation */
const SIMULATION_NAME_ALLOWED = /[^a-zA-Z0-9 _\-.,()[\]#@+]/g;

/**
 * Sanitizes a free-text search string for use in HTTP parameters.
 * Keeps alphanumeric characters, spaces, and common safe punctuation.
 */
export function sanitizeSearchString(value: string | null | undefined): string {
  if (!value) return '';
  return value.replace(SEARCH_STRING_ALLOWED, '');
}

/**
 * Sanitizes an identifier-style parameter (e.g. FilterBy, SortDirection,
 * SortColumnName) which should only contain alphanumeric chars, hyphens
 * and underscores.
 */
export function sanitizeIdentifier(value: string | null | undefined): string {
  if (!value) return '';
  return value.replace(IDENTIFIER_ALLOWED, '');
}

/**
 * Sanitizes a rock file name for use in HTTP parameters.
 * Keeps alphanumeric characters, dots, underscores and hyphens.
 */
export function sanitizeFileName(value: string | null | undefined): string {
  if (!value) return '';
  return value.replace(FILENAME_ALLOWED, '');
}

/**
 * Sanitizes a user-supplied simulation name for use in HTTP parameters.
 * Keeps alphanumeric characters, spaces and common safe punctuation.
 */
export function sanitizeSimulationName(value: string | null | undefined): string {
  if (!value) return '';
  return value.replace(SIMULATION_NAME_ALLOWED, '');
}
